import React from "react";
import { useTranslate } from "@refinedev/core";
import { Col, Row } from "antd";
import {
  useProjectKpis,
  type ProjectKpis,
} from "../../projects/hooks/useProjectKpis";
import type { AnalyticsDateRange } from "../../projects/hooks/useAnalyticsDateRange";
import { CompactStatCard } from "./CompactStatCard";
import { TARGETS } from "./constants/constants";
import { getMetricLevel, metricColors } from "./utils/";

const T_PREFIX = "media.projects.analytics.kpis";

type KpiCard = {
  key: keyof ProjectKpis;
  type: "number" | "percentage" | "seconds";
  target?: number;
};

const CARDS: KpiCard[] = [
  { key: "totalViews", type: "number" },
  {
    key: "avgReadDepthPct",
    type: "percentage",
    target: TARGETS.documentHighlighter.avg_reading_depth_pct,
  },
  {
    key: "medianDropSeconds",
    type: "seconds",
    target: TARGETS.videoExplainer.median_drop_seconds,
  },
  {
    key: "completionRatePct",
    type: "percentage",
    target: TARGETS.videoExplainer.full_completers_pct,
  },
];

// Only a figure below its target gets a color, without a status line.
const colorIfMissed = (value: number | null, target?: number) => {
  if (value == null || target === undefined) return undefined;
  const level = getMetricLevel(value, target);
  return level === "success" ? undefined : metricColors[level];
};

export type ProjectKpiRowProps = {
  projectId: string | undefined;
  dateRange: AnalyticsDateRange;
  /** The version picked on the page, if any. */
  version?: string;
};

/** Headline figures shown above every analytics tab. */
export const ProjectKpiRow: React.FC<ProjectKpiRowProps> = ({
  projectId,
  dateRange,
  version,
}) => {
  const t = useTranslate();
  const kpis = useProjectKpis(projectId, dateRange, version);

  return (
    <Row gutter={[12, 12]}>
      {CARDS.map(({ key, type, target }) => (
        <Col key={key} xs={24} sm={12} xl={6}>
          <CompactStatCard
            label={t(`${T_PREFIX}.${key}.label`)}
            subtitle={t(`${T_PREFIX}.${key}.hint`)}
            metric={kpis[key].value}
            type={type}
            valueColor={colorIfMissed(kpis[key].value, target)}
            loading={kpis[key].isLoading}
          />
        </Col>
      ))}
    </Row>
  );
};
